/*
 * @Author: Ryan
 * @Date: 2026-02-02
 * @Description: Frequently Asked Questions section for AI Image Translator
 */
import React, { useState } from 'react';

interface FAQItem {
  id: string;
  question: string;
  answer: string | React.ReactNode;
}

interface FAQItemProps {
  item: FAQItem;
  isExpanded: boolean;
  onClick: () => void;
}

const FAQItemComponent: React.FC<FAQItemProps> = ({ item, isExpanded, onClick }) => {
  return (
    <div
      className={`
        border border-[#CDCDCD] transition-all duration-300 cursor-pointer
        ${isExpanded ? 'bg-white' : 'bg-white hover:bg-gray-50'}
      `}
      onClick={onClick}
    >
      <div className="px-6 py-6 pad:px-8 pad:py-8">
        {/* 问题行 */}
        <div className="flex items-center justify-between gap-4">
          {/* 问题文本 */}
          <h3 className="flex-1 text-base pad:text-base font-semibold text-[#0F1117]">
            {item.question}
          </h3>

          {/* +/- 图标 */}
          <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
            {isExpanded ? (
              <svg
                className="w-5 h-5 text-[#0F1117]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20 12H4"
                />
              </svg>
            ) : (
              <svg
                className="w-5 h-5 text-[#0F1117]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
            )}
          </div>
        </div>

        {/* 答案 - 展开时显示 */}
        <div
          className={`
            overflow-hidden transition-all duration-300
            ${isExpanded ? 'max-h-[500px] opacity-100 mt-4' : 'max-h-0 opacity-0'}
          `}
        >
          <div className="text-sm pad:text-base text-gray-600 leading-relaxed">
            {item.answer}
          </div>
        </div>
      </div>
    </div>
  );
};

const FAQSection: React.FC = () => {
  // 使用 Set 来跟踪展开的问题 ID，允许同时展开多个
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set(['faq-1']));

  const faqItems: FAQItem[] = [
    {
      id: 'faq-1',
      question: 'What is image translation?',
      answer:
        "Image translation is the process of extracting text from an image and converting it into another language while preserving the original layout and design. Unlike simple text translation, image translation handles visual elements like fonts, colors, and positioning to keep the translated content looking natural and professional. It's useful for translating product labels, menus, signs, documents, and other visual content.",
    },
    {
      id: 'faq-2',
      question: 'What languages does FalcoCut AI Image Translator support?',
      answer: (
        <>
          FalcoCut AI Image Translator supports over 30 languages, including:
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>
              <strong>European languages:</strong> English, Spanish, French, German,
              Italian, Portuguese, Dutch, Polish, Russian, Ukrainian, and more
            </li>
            <li>
              <strong>Asian languages:</strong> Chinese (Simplified & Traditional),
              Japanese, Korean, Thai, Vietnamese, Indonesian, Hindi, and more
            </li>
            <li>
              <strong>Middle Eastern languages:</strong> Arabic, Turkish, Hebrew, and more
            </li>
          </ul>
          <p className="mt-2">
            The full list continues to expand as we add support for more languages.
          </p>
        </>
      ),
    },
    {
      id: 'faq-3',
      question: 'Is my data safe?',
      answer:
        'Yes, your data is secure with FalcoCut. We use industry-standard encryption to protect your images during upload and processing. Your files are not stored permanently—they are automatically deleted after processing is complete. We do not share your images or translations with third parties. For enterprise users with stricter requirements, we offer additional security options.',
    },
    {
      id: 'faq-4',
      question: 'Can I translate multiple images at once?',
      answer:
        'Yes, FalcoCut supports batch processing, allowing you to upload and translate multiple images simultaneously. This feature is especially useful for e-commerce sellers, content creators, and businesses that need to localize large volumes of visual content quickly. Simply upload your images, select the target language, and let the AI handle the rest.',
    },
    {
      id: 'faq-5',
      question: 'Does the image translator support handwritten text?',
      answer:
        "FalcoCut's AI can recognize and translate handwritten text in many cases, though accuracy depends on the legibility of the handwriting. Clear, well-formed handwriting typically yields good results, while highly stylized or cursive scripts may be more challenging. For best results with handwritten content, ensure the image is well-lit and the text is clearly visible.",
    },
    {
      id: 'faq-6',
      question: 'How accurate is the translation?',
      answer: (
        <>
          FalcoCut uses advanced AI models trained on millions of translation pairs to
          deliver high-quality results. Translation accuracy depends on several factors:
          <ol className="list-decimal pl-5 mt-2 space-y-1">
            <li>
              <strong>Text clarity:</strong> Clear, readable text produces better results
            </li>
            <li>
              <strong>Language pair:</strong> Common language pairs (like English-Spanish)
              tend to be more accurate
            </li>
            <li>
              <strong>Context:</strong> Technical or specialized content may require
              review
            </li>
            <li>
              <strong>Image quality:</strong> Higher resolution images yield better text
              extraction
            </li>
          </ol>
          <p className="mt-2">
            Our built-in review tools let you verify and adjust translations before
            finalizing, ensuring accuracy for your specific needs.
          </p>
        </>
      ),
    },
    {
      id: 'faq-7',
      question: 'Can I edit the translation before saving?',
      answer:
        "Yes, FalcoCut provides built-in review and editing tools that let you refine translations before saving. After the AI generates the translated image, you can review each text block, make corrections, adjust positioning, and ensure everything looks perfect. This gives you full control over the final output while still benefiting from the AI's speed and automation.",
    },
  ];

  const handleItemClick = (id: string) => {
    setExpandedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  return (
    <section className="w-full bg-white py-12 pad:py-16 sm:py-20 px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* 标题 */}
        <h2 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-[#0F1117] text-center mb-10 pad:mb-12 sm:mb-16">
          Frequently Asked Questions
        </h2>

        {/* FAQ 列表 - 840px 宽度居中 */}
        <div className="max-w-[840px] mx-auto flex flex-col gap-2">
          {faqItems.map((item) => (
            <FAQItemComponent
              key={item.id}
              item={item}
              isExpanded={expandedIds.has(item.id)}
              onClick={() => handleItemClick(item.id)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
